import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme } from '@mui/material';
import './styles/MobileSettings.css';
import { useTutorial } from '../../../components/tutorial/TutorialContext';
import { usePermissions } from '../../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../../company-management/constants/permissions';
import { useAuth } from '../../../context/AuthContext';

// Import the same page components as the desktop version
import AccountSettings from '../pages/AccountSettings';
import ManageSubscription from '../pages/billing/ManageSubscription';
import BusinessInformation from '../pages/BusinessInformation';
import MobileBusinessHours from './MobileBusinessHours';
import AuthorizeSettings from '../pages/AuthorizeSettings';
import DeluxeSettings from '../pages/DeluxeSettings';
import PaymentSettingsLanding from '../pages/PaymentSettingsLanding';
import BookingSettings from '../pages/booking/BookingSettings';
import NotificationsRemindersSettings from '../pages/notifications-reminders/NotificationsRemindersSettings';
import ServicesCategoriesSettings from '../pages/services-categories/ServicesCategoriesSettings';
import LandingPageSettings from '../pages/LandingPageSettings';
import ClientExperienceSettings from '../pages/client-experience/ClientExperienceSettings';
import PaymentGatewayEnvironmentSettings from '../pages/payment-environment/PaymentGatewayEnvironmentSettings';
import MyServicesSettingsPage from '../pages/MyServicesSettingsPage';
import LocationsSettingsPage from '../../../locations/settings/LocationsSettingsPage';
import { parseLocationSettingsRoute } from '../../../locations/settings/locationSettingsPaths';
import {
  buildSettingsSectionPath,
  getCanonicalSettingsHref,
  getSettingsPathFromLocation,
  isSettingsSectionHome,
  parseSettingsSectionPath,
} from '../section-paths/settingsSectionPaths';

// Menu items are now dynamically generated based on permissions

const MobileSettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialSection = parseSettingsSectionPath(location.pathname, location.search);
  const [selectedSection, setSelectedSection] = useState(initialSection.section);
  const [selectedParentSection, setSelectedParentSection] = useState(initialSection.parent);
  const [notificationsSubtab, setNotificationsSubtab] = useState(initialSection.notificationsTab || 'email');
  const [hasUnsavedSettingsChanges, setHasUnsavedSettingsChanges] = useState(false);
  const [isNavVisible, setIsNavVisible] = useState(
    isSettingsSectionHome(location.pathname, location.search) && !location.state?.section
  );
  const [menuQuery, setMenuQuery] = useState('');
  const [expandedParents, setExpandedParents] = useState({});
  const theme = useTheme();
  const isDarkMode = theme?.palette?.mode === 'dark';
  const { isDismissed, restoreTutorial } = useTutorial();
  const { hasPermission } = usePermissions();
  const { user, companyData } = useAuth();
  const showMyServices =
    companyData?.settings?.allowEmployeeSelfServiceAssignment === true &&
    String(user?.role || '').toLowerCase() !== 'owner';

  // Create dynamic menu items based on permissions
  const getMenuItems = () => {
    const baseMenuItems = [
      {
        id: 'account',
        group: 'Personal',
        label: 'Account Settings',
        description: 'Manage your profile, sign-in, and privacy preferences'
      },
      ...(String(user?.role || '').toLowerCase() === 'owner'
        ? [
            {
              id: 'billing',
              group: 'Personal',
              label: 'Billing',
              description: 'Manage your subscription, payment method, and invoices',
            },
          ]
        : []),
      ...(showMyServices
        ? [
            {
              id: 'my-services',
              group: 'Personal',
              label: 'My Services',
              description: 'Choose which services you are available to perform',
            },
          ]
        : []),
      {
        id: 'business',
        group: 'Business',
        label: 'Business Information',
        description: 'Update your business details and contact information'
      },
      {
        id: 'hours',
        group: 'Business',
        label: 'Business Hours',
        description: 'Set your working hours and availability'
      },
      {
        id: 'locations',
        group: 'Business',
        label: 'Locations',
        description: 'Manage business locations, addresses, and site hours'
      },
      {
        id: 'booking',
        group: 'Business',
        label: 'Booking Settings',
        description: 'Control booking rules like double booking'
      },
      {
        id: 'services-categories',
        group: 'Business',
        label: 'Services',
        description: 'Manage your bookable services and pricing',
      },
      {
        id: 'client-experience',
        group: 'Business',
        label: 'Client Experience',
        description: 'Manage your website and shared client-facing links'
      },
      {
        id: 'payments',
        group: 'Payments',
        label: 'Payment Settings',
        description: 'Manage payment methods and integrations'
      },
      ...(hasPermission(PERMISSIONS.SETTINGS.EDIT)
        ? [
            {
              id: 'notifications',
              group: 'Messaging',
              label: 'Notifications & Reminders',
              description: 'Customize email/sms notifications and templates',
            },
          ]
        : []),
    ];

    return baseMenuItems;
  };

  const menuItems = getMenuItems();

  const filteredMenuItems = useMemo(() => {
    const q = menuQuery.trim().toLowerCase();
    if (!q) return menuItems;

    return menuItems.filter((item) => {
      const inTopLevel =
        item.label.toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q) ||
        (item.group || '').toLowerCase().includes(q);

      if (inTopLevel) return true;
      if (!item.subItems?.length) return false;
      return item.subItems.some((subItem) => subItem.label.toLowerCase().includes(q));
    });
  }, [menuItems, menuQuery]);

  const groupedMenuItems = useMemo(() => {
    return filteredMenuItems.reduce((acc, item) => {
      const group = item.group || 'Other';
      if (!acc[group]) acc[group] = [];
      acc[group].push(item);
      return acc;
    }, {});
  }, [filteredMenuItems]);

  const groupOrder = ['Personal', 'Business', 'Payments', 'Messaging', 'Other'];

  const confirmSettingsLeave = () => {
    if (!hasUnsavedSettingsChanges) {
      return true;
    }

    const shouldLeave = window.confirm('You have unsaved changes. Save them before leaving this page?');
    if (shouldLeave) {
      setHasUnsavedSettingsChanges(false);
    }

    return shouldLeave;
  };
  
  // Ensure we scroll to the very top after content changes render
  const scrollToTopOfPage = () => {
    const perform = () => {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch {
          window.scrollTo(0, 0);
        }
      }
      if (typeof document !== 'undefined') {
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      }
    };
    if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
      window.requestAnimationFrame(perform);
    } else {
      setTimeout(perform, 0);
    }
  };

  // Match desktop: scroll when section changes
  useEffect(() => {
    scrollToTopOfPage();
  }, [selectedSection]);

  // Also scroll when entering a section view from the nav
  useEffect(() => {
    if (!isNavVisible) {
      scrollToTopOfPage();
    }
  }, [isNavVisible]);

  // When going back to the nav, ensure the active parent is expanded
  useEffect(() => {
    if (isNavVisible) {
      setExpandedParents((prev) => ({
        ...prev,
        [selectedParentSection]: true,
      }));
    }
  }, [isNavVisible, selectedParentSection]);

  const openSettingsSection = (section, { tab, replace = false, state, showNav = false } = {}) => {
    const nextPath = buildSettingsSectionPath(section, { tab });
    setIsNavVisible(showNav);
    if (nextPath === getCanonicalSettingsHref(location.pathname, location.search)) {
      return;
    }
    navigate(nextPath, { replace, state });
  };

  useEffect(() => {
    const next = getSettingsPathFromLocation(location);
    const currentHref = `${location.pathname}${location.search}`;

    if (!parseLocationSettingsRoute(location.pathname) && next.path !== currentHref) {
      navigate(next.path, { replace: true, state: location.state });
      setIsNavVisible(false);
      return;
    }

    setSelectedSection(next.section);
    setSelectedParentSection(next.parent);
    if (next.notificationsTab) {
      setNotificationsSubtab(next.notificationsTab);
    }
    if (!isSettingsSectionHome(location.pathname, location.search) || next.fromState) {
      setIsNavVisible(false);
    }
  }, [location, navigate]);

  const handleMenuClick = (item) => {
    if (!confirmSettingsLeave()) {
      return;
    }

    if (item?.subItems?.length) {
      setSelectedParentSection(item.id);
      // Toggle expand/collapse and collapse all other expanded items
      setExpandedParents((prev) => {
        const isExpanded = !!prev[item.id];
        const newExpanded = {};

        // Close all other expanded items
        Object.keys(prev).forEach((key) => {
          if (key !== item.id) {
            newExpanded[key] = false;
          }
        });

        // Set the clicked item's expanded state
        newExpanded[item.id] = !isExpanded;

        return newExpanded;
      });
      return;
    }

    // When clicking on an item without subitems, collapse all expanded items
    setExpandedParents({});
    if (item.id === 'notifications') {
      setNotificationsSubtab('email');
    }
    openSettingsSection(item.id, { tab: item.id === 'notifications' ? 'email' : undefined });
  };

  const goBackToNav = () => {
    if (!confirmSettingsLeave()) {
      return;
    }

    setIsNavVisible(true);
  };

  const getContent = () => {
    switch (selectedSection) {
      case 'account':
        return <AccountSettings />;
      case 'billing':
        return String(user?.role || '').toLowerCase() === 'owner'
          ? <ManageSubscription embedded />
          : null;
      case 'my-services':
        return <MyServicesSettingsPage />;
      case 'profile':
        return <AccountSettings />;
      case 'business':
        return <BusinessInformation />;
      case 'hours':
        return <MobileBusinessHours />;
      case 'locations':
        return <LocationsSettingsPage />;
      case 'booking':
        return <BookingSettings />;
      case 'client-experience':
      case 'portal':
        return <ClientExperienceSettings onUnsavedChangesChange={setHasUnsavedSettingsChanges} />;
      case 'payments':
      case 'deposits':
      case 'tips':
        return (
          <PaymentSettingsLanding 
            onSelectOption={(section) => {
              openSettingsSection(section);
            }}
          />
        );
      case 'deluxe':
        return (
          <DeluxeSettings
            onBackToPayments={() => {
              openSettingsSection('payments');
            }}
          />
        );
      case 'authorize':
        return (
          <AuthorizeSettings 
            onBackToPayments={() => {
              openSettingsSection('payments');
            }}
          />
        );
      case 'gateway-environments':
        if (!user?.isSpecialAdmin) {
          return (
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" color="text.secondary">
                This section is available to special admins only.
              </Typography>
            </Box>
          );
        }
        return (
          <PaymentGatewayEnvironmentSettings
            onBackToPayments={() => {
              openSettingsSection('payments');
            }}
          />
        );
      case 'services-categories':
        return hasPermission(PERMISSIONS.SETTINGS.EDIT) ? <ServicesCategoriesSettings /> : null;
      case 'notifications':
        return (
          <NotificationsRemindersSettings
            messagingTab={notificationsSubtab}
            onMessagingTabChange={(tab) => {
              setNotificationsSubtab(tab);
              openSettingsSection('notifications', { tab, replace: true, state: location.state });
            }}
          />
        );
      case 'landing-page':
        return <LandingPageSettings onUnsavedChangesChange={setHasUnsavedSettingsChanges} />;
      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              {menuItems.find(item => item.id === selectedSection)?.label}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Content for {selectedSection} will go here
            </Typography>
          </Box>
        );
    }
  };

  const isBusinessHoursDetail = !isNavVisible && selectedSection === 'hours';

  return (
    <div
      className={`mobile-settings-page ${isDarkMode ? 'is-dark' : ''}${
        isBusinessHoursDetail ? ' mobile-settings-page--business-hours-detail' : ''
      }`}
    >
      <div className="mobile-settings-container">
        {!isNavVisible && (
          <div className="mobile-topbar">
            <button className="back-btn" onClick={goBackToNav}>
              ← Back to Settings
            </button>
          </div>
        )}

        {isNavVisible ? (
          <div className="mobile-settings-nav-card">
            <div className="m-settings-header">
              <div className="m-settings-title">Settings</div>
              <div className="m-settings-subtitle">Account, business, payments, and reminders</div>
              <label className="m-settings-search" aria-label="Search settings">
                <input
                  value={menuQuery}
                  onChange={(e) => setMenuQuery(e.target.value)}
                  placeholder="Search settings…"
                  type="search"
                />
              </label>
            </div>

            {filteredMenuItems.length === 0 ? (
              <div className="m-settings-empty">
                <div className="m-settings-empty-title">No matches</div>
                <div className="m-settings-empty-subtitle">Try a different keyword.</div>
              </div>
            ) : (
              <div className="m-settings-groups">
                {groupOrder.map((group) => {
                  const items = groupedMenuItems[group];
                  if (!items?.length) return null;

                  return (
                    <section key={group} className="m-settings-group" aria-label={group}>
                      <div className="m-settings-group-title">{group}</div>
                      <ul className="m-settings-list">
                        {items.map((item) => {
                          const isActiveParent = selectedParentSection === item.id;
                          const q = menuQuery.trim().toLowerCase();
                          const subItemsToShow = !item.subItems?.length
                            ? []
                            : !q
                              ? item.subItems
                              : item.subItems.filter((s) => s.label.toLowerCase().includes(q));

                          const hasSubItems = item.subItems && item.subItems.length > 0;
                          const isExpanded = !!expandedParents[item.id];
                          const showSubItems = hasSubItems && (isExpanded || isActiveParent || !!q);

                          return (
                            <li key={item.id} className="m-settings-item">
                              <button
                                className={`m-settings-button ${isActiveParent ? 'active' : ''}`}
                                onClick={() => handleMenuClick(item)}
                                type="button"
                                aria-current={isActiveParent ? 'page' : undefined}
                              >
                                <span className="m-settings-text">
                                  <span className="m-settings-label">{item.label}</span>
                                  {item.description && <span className="m-settings-desc">{item.description}</span>}
                                </span>
                                {!!item.subItems?.length && (
                                  <span className={`m-settings-chevron ${showSubItems ? 'open' : ''}`} aria-hidden="true">
                                    ▾
                                  </span>
                                )}
                              </button>

                              {showSubItems && subItemsToShow.length > 0 && (
                                <ul className="m-settings-sublist" aria-label={`${item.label} pages`}>
                                  {subItemsToShow.map((subItem) => (
                                    <li key={subItem.id} className="m-settings-subitem">
                                      <button
                                        className={`m-settings-subbutton ${selectedSection === subItem.id ? 'active' : ''}`}
                                        onClick={() => {
                                          if (!confirmSettingsLeave()) {
                                            return;
                                          }
                                          openSettingsSection(subItem.id);
                                        }}
                                        type="button"
                                        aria-current={selectedSection === subItem.id ? 'page' : undefined}
                                      >
                                        {subItem.label}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          );
                        })}

                        {(() => {
                          const isOwner = (user?.role || '').toLowerCase() === 'owner';
                          const canAdminSettings = isOwner || hasPermission(PERMISSIONS.SETTINGS.EDIT) || hasPermission(PERMISSIONS.SETTINGS.VIEW_ALL);
                          const canShowSetupGuide = isDismissed && canAdminSettings;
                          return canShowSetupGuide;
                        })() && group === 'Personal' && (
                          <li className="m-settings-item">
                            <button
                              type="button"
                              onClick={restoreTutorial}
                              className="m-settings-button"
                            >
                              <span className="m-settings-text">
                                <span className="m-settings-label">Show Setup Guide</span>
                                <span className="m-settings-desc">Restore the setup guide to help you configure your settings</span>
                              </span>
                            </button>
                          </li>
                        )}
                      </ul>
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className={`mobile-settings-card ${( 
            selectedSection === 'account' ||
            selectedSection === 'billing' ||
            selectedSection === 'profile' ||
            selectedSection === 'business' ||
            selectedSection === 'hours' ||
            selectedSection === 'portal' ||
            selectedSection === 'client-experience' ||
            selectedSection === 'landing-page' ||
            selectedSection === 'services-categories' ||
            selectedSection === 'deluxe' ||
            selectedSection === 'authorize' ||
            selectedSection === 'gateway-environments' ||
            selectedSection === 'deposits' ||
            selectedSection === 'tips' ||
            selectedSection === 'notifications' ||
            selectedSection === 'payments'
          ) ? 'transparent' : ''}`}>
            <div className={`mobile-content-card ${( 
              selectedSection === 'account' ||
              selectedSection === 'billing' ||
              selectedSection === 'profile' ||
              selectedSection === 'business' ||
              selectedSection === 'hours' ||
              selectedSection === 'portal' ||
              selectedSection === 'client-experience' ||
              selectedSection === 'landing-page' ||
              selectedSection === 'services-categories' ||
              selectedSection === 'deluxe' ||
            selectedSection === 'authorize' ||
              selectedSection === 'gateway-environments' ||
              selectedSection === 'deposits' ||
              selectedSection === 'tips' ||
              selectedSection === 'notifications' ||
              selectedSection === 'payments'
            ) ? 'transparent' : ''}`}>
              {getContent()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileSettingsPage; 